import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import SlideOver from "../../../components/slideOver/SlideOver";
import SetupRecordForm from "../../../components/forms/setup/SetupRecordForm";
import { receivingApi } from "../../../shared/receivingApi";
import { useToast } from "../../../contexts/ToastContext";
import type { SetupRecordValues } from "../../../components/forms/setup/SetupRecordForm";
import type { ApiErrorPayload } from "../../../types/api";
import type { Supplier, SupplierPayload } from "../../../types/receiving";

interface SlideOverState {
  open: boolean;
  childComponent: ReactNode;
}

const closedPanel: SlideOverState = { open: false, childComponent: null };
const fields = [
  { name: "name", label: "Supplier name", placeholder: "Supplier name", required: true },
  { name: "supplier_number", label: "Supplier number", placeholder: "Optional supplier reference" },
];

const apiErrorMessage = (error: ApiErrorPayload, fallback: string) =>
  error.message ?? Object.values(error.errors ?? {}).flat()[0] ?? fallback;

const supplierPayload = (values: SetupRecordValues): SupplierPayload => ({
  name: String(values.name).trim(),
  supplier_number: String(values.supplier_number).trim() || null,
  status: Boolean(values.status),
});

export default function Suppliers() {
  const { showSuccess } = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [slideOver, setSlideOver] = useState<SlideOverState>(closedPanel);

  const load = async (searchTerm = search) => {
    setLoading(true);
    setError("");
    const result = await receivingApi.supplierList(searchTerm);
    if (result.success) setSuppliers(result.data.data);
    else setError(result.data?.message || "Could not load suppliers.");
    setLoading(false);
  };
  useEffect(() => {
    void load("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => setSlideOver(closedPanel);

  const open = (supplier: Supplier | null = null) =>
    setSlideOver({
      open: true,
      childComponent: (
        <SetupRecordForm
          title={supplier ? "Edit supplier" : "Add supplier"}
          description="Supplier identity used on receiving invoices."
          record={supplier}
          fields={fields}
          onCancel={close}
          onSave={async (values) => {
            const payload = supplierPayload(values);
            const result = supplier
              ? await receivingApi.updateSupplier(supplier.id, payload)
              : await receivingApi.createSupplier(payload);
            if (!result.success)
              return {
                success: false,
                message: apiErrorMessage(result.data, "Could not save supplier."),
              };
            showSuccess(supplier ? "Supplier updated" : "Supplier created");
            close();
            await load();
            return { success: true };
          }}
        />
      ),
    });

  return (
    <div className="mt-2 px-4 sm:px-6 lg:px-8">
      <div className="card-custom px-4 py-5">
        <h1 className="text-base font-semibold text-gray-900">Suppliers</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage suppliers available to receiving invoices.
        </p>
        <div className="mt-5 flex rounded-md">
          <div className="relative flex flex-grow items-stretch focus-within:z-10">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") void load();
              }}
              placeholder="Search by supplier name or number"
              className="block w-full rounded-lg rounded-l-md rounded-br-none rounded-tr-none border border-gray-300 pl-2 sm:text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1"
          >
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </button>
          <button
            type="button"
            onClick={() => open()}
            className="ml-5 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
          >
            Add supplier
          </button>
        </div>
      </div>
      {error && (
        <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="mt-8 overflow-hidden card-custom">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Loading suppliers…</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-3 py-3 text-left text-sm font-semibold">Supplier number</th>
                <th className="px-3 py-3 text-left text-sm font-semibold">Status</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {suppliers.length ? (
                suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td className="px-6 py-4 text-sm font-medium">{supplier.name}</td>
                    <td className="px-3 py-4 font-mono text-sm text-gray-500">
                      {supplier.supplier_number || "—"}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {supplier.status ? "Active" : "Inactive"}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => open(supplier)}
                        className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-10 text-center text-sm text-gray-500">
                    No suppliers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}
